import { Button, EmptyState } from "@nodus/ui";
import { createFileRoute, Outlet, redirect, useParams } from "@tanstack/react-router";

import { OrgFrame } from "../../app/org-frame";
import { consoleMask } from "../../app/console-url";
import { lastScope } from "../../app/scope";
import { loadOrg } from "../../app/org-load";

// `/$org`: the tab's org comes from its URL and every request of this tab sends it as Nodus-Org (ADR-107).
export const Route = createFileRoute("/_authed/$org")({
  beforeLoad: async ({ context, params, location }) => {
    if (params.org !== "~") return;
    const userId = context.session.user.id;
    const orgs = await context.queryClient.fetchQuery({ queryKey: ["user", "orgs", userId], queryFn: context.api.organizations });
    if (!orgs.length) throw redirect({ to: "/onboarding" });
    const last = lastScope(userId);
    const org = orgs.find((o) => o.metadata.name === last.org)?.metadata.name ?? orgs[0]!.metadata.name;
    const parts = location.pathname.split("/");
    parts[1] = encodeURIComponent(org);
    if (parts[2] === "~") parts[2] = encodeURIComponent(last.project(org));
    const href = `${parts.join("/")}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`;
    throw redirect({ href, mask: consoleMask(href), replace: true });
  },
  loader: ({ context, params }) => loadOrg(context, params.org),
  component: OrgLayout,
});

function OrgLayout() {
  const data = Route.useLoaderData();
  const params = useParams({ strict: false }) as { project?: string };
  if (data.missing) {
    return (
      <main className="px-6 py-16">
        <EmptyState
          title="Organization not found"
          description="It does not exist, or you are not a member. Ask an Owner for an invite."
          actions={<Button onClick={() => window.location.assign("/")}>Go to your organizations</Button>}
        />
      </main>
    );
  }
  // The shell (switcher, rail links) names only projects the org has, even on a catalog page under `nodus`.
  return (
    <OrgFrame data={data} project={params.project}>
      <Outlet />
    </OrgFrame>
  );
}
